"use client";

import { Link, Typography, Container } from "@cappelendamm/design-system/react";
import type { CSSProperties, KeyboardEvent } from "react";
import { type ReactNode, useState } from "react";

type Tab = {
  key: string;
  title: string;
  content: ReactNode;
};

type TabStyle = {
  selectedBackgroundColor?: { hex?: string | null } | null;
  selectedTextColor?: { hex?: string | null } | null;
  unselectedBackgroundColor?: { hex?: string | null } | null;
  unselectedTextColor?: { hex?: string | null } | null;
  unselectedBorderColor?: { hex?: string | null } | null;
  borderRadius?: string | null;
};

type TabbedSectionTabsProps = {
  tabs: Tab[];
  style?: TabStyle | null;
  sectionId: string;
};

export function TabbedSectionTabs({
  tabs,
  style,
  sectionId,
}: TabbedSectionTabsProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!tabs.length) return null;

  const selectedBackgroundColor =
    style?.selectedBackgroundColor?.hex ?? undefined;
  const selectedTextColor = style?.selectedTextColor?.hex ?? undefined;
  const unselectedBackgroundColor =
    style?.unselectedBackgroundColor?.hex ?? undefined;
  const unselectedTextColor = style?.unselectedTextColor?.hex ?? undefined;
  const unselectedBorderColor =
    style?.unselectedBorderColor?.hex ?? "var(--fg-body)";
  const borderRadius = style?.borderRadius ?? undefined;
  const idScope = sectionId.replace(/[^a-zA-Z0-9_-]/g, "-");

  const handleTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (!tabs.length) return;

    if (event.key === "ArrowRight") {
      event.preventDefault();
      setActiveIndex((index + 1) % tabs.length);
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setActiveIndex((index - 1 + tabs.length) % tabs.length);
    }
    if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
    }
    if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(tabs.length - 1);
    }
  };

  return (
    <div className="w-full">
      <Container>
        <ul
          role="tablist"
          aria-label="Faner"
          aria-orientation="horizontal"
          className="flex w-full snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth p-1 @lg:flex-wrap"
        >
          {tabs.map((tab, i) => {
            const isActive = i === activeIndex;
            const currentBackgroundColor = isActive
              ? selectedBackgroundColor
              : unselectedBackgroundColor;
            const currentTextColor = isActive
              ? selectedTextColor
              : unselectedTextColor;

            const background = isActive
              ? (currentBackgroundColor ?? "var(--bg-primary)")
              : (currentBackgroundColor ?? "var(--bg-body)");
            const borderColor = isActive ? background : unselectedBorderColor;

            const tabStyle = {
              ...(borderRadius ? { borderRadius } : {}),
              "--cvr-link-navigation-border": `2px solid ${borderColor}`,
              "--cvr-link-navigation-border-hover": borderColor,
              "--cvr-link-navigation-bg-hover": isActive
                ? background
                : "var(--bg-panel)",
              "--cvr-link-navigation-background": background,
              ...(currentTextColor
                ? { "--cvr-link-navigation-contrast": currentTextColor }
                : {}),
            } as CSSProperties;

            return (
              <li key={tab.key} className="snap-center">
                <Link
                  as="button"
                  type="button"
                  role="tab"
                  tabIndex={isActive ? 0 : -1}
                  aria-selected={isActive}
                  aria-controls={`tabpanel-${idScope}-${tab.key}`}
                  id={`tab-${idScope}-${tab.key}`}
                  onClick={() => setActiveIndex(i)}
                  onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) =>
                    handleTabKeyDown(event, i)
                  }
                  variant="navigation"
                  style={tabStyle}
                >
                  <Typography
                    as="span"
                    variant="body-xs"
                    className="text-nowrap"
                  >
                    {tab.title}
                  </Typography>
                </Link>
              </li>
            );
          })}
        </ul>
      </Container>

      {tabs.map((tab, i) => (
        <div
          key={tab.key}
          role="tabpanel"
          id={`tabpanel-${idScope}-${tab.key}`}
          aria-labelledby={`tab-${idScope}-${tab.key}`}
          hidden={i !== activeIndex}
        >
          <div className="grid cvr-section-spacing">{tab.content}</div>
        </div>
      ))}
    </div>
  );
}
